import DOMPurify from 'dompurify';
import ConditionalsComponent from '../../implant/conditionals/ConditionalsComponent';
import { ShieldAlert } from 'lucide-react';
import SETTING_KEYS from '../../../constants/setting-keys';
import type { PolicyConditionalsValue } from './types';
import { hasPolicyConditionalsContent } from './conditionalsUtils';
import { cn } from '../../../lib/utils';

function PolicyConditionalsAlert({
    title,
    value,
    color = 'blue',
}: {
    title: string;
    value: string;
    color?: 'blue' | 'amber';
}) {
    const sanitized = DOMPurify.sanitize(value);
    const tone =
        color === 'amber'
            ? {
                  wrapper: 'border-amber-700/50 bg-amber-950/20',
                  header:
                      'border-amber-700/50 bg-amber-900/35 text-amber-100',
                  body: 'text-amber-50/85',
              }
            : {
                  wrapper: 'border-sky-700/50 bg-sky-950/30',
                  header:
                      'border-sky-700/50 bg-sky-900/40 text-sky-100',
                  body: 'text-sky-50/85',
              };

    return (
        <section
            className={cn(
                'overflow-hidden rounded-xl border',
                tone.wrapper
            )}
        >
            <div
                className={cn(
                    'flex items-center gap-2.5 border-b px-4 py-3',
                    tone.header
                )}
            >
                <ShieldAlert className="h-4 w-4 shrink-0" />
                <h5 className="m-0 flex items-center text-[15px] font-semibold leading-none tracking-[0.01em]">
                    {title}
                </h5>
            </div>
            <div
                className={cn(
                    'px-4 py-3 text-base leading-8 whitespace-pre-line [&_p]:m-0 [&_p+p]:mt-2',
                    tone.body
                )}
                dangerouslySetInnerHTML={{ __html: sanitized }}
            />
        </section>
    );
}

const ALERT_FIELDS: { key: typeof SETTING_KEYS.addInfo | typeof SETTING_KEYS.prep; label: string; color: 'blue' | 'amber' }[] = [
    { key: SETTING_KEYS.addInfo, label: 'Precaution', color: 'blue' },
    { key: SETTING_KEYS.prep, label: 'Preparation', color: 'amber' },
];

export function PolicyConditionalsView({
    value,
}: {
    value?: PolicyConditionalsValue;
}) {
    if (!value || !hasPolicyConditionalsContent(value)) return null;

    const alerts = ALERT_FIELDS
        .map(({ key, label, color }) => ({ title: label, value: (value[key] as string) ?? '', color, key }))
        .filter(({ value: v }) => !!v?.trim());

    // Excluding "prep" because we show it in amber alert above
    const conditionalsForGenericRenderer = Object.fromEntries(
        Object.entries(value).filter(([key]) => key !== SETTING_KEYS.prep)
    );

    return (
        <div className="space-y-10">
            {alerts.length > 0 && (
                <div className="space-y-6">
                    {alerts.map((field) => (
                        <PolicyConditionalsAlert
                            key={field.key}
                            title={field.title}
                            value={field.value}
                            color={field.color}
                        />
                    ))}
                </div>
            )}
            <ConditionalsComponent conditionals={conditionalsForGenericRenderer} />
        </div>
    );
}
